import { useRef, useState } from 'react'
import {
  Box, Button, Chip, Tooltip, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  IconButton, Typography, Snackbar,
} from '@mui/material'
import { Close, LockOpen, TaskAlt, CheckCircle, WarningAmber } from '@mui/icons-material'
import { useApi } from '../../hooks/useApi'
import { useAuth } from '../../hooks/useAuth'
import { basePlanPillLabel } from './boardShell'
import {
  APPROVAL_TAB_NAMES, NOT_ALLOWED_TOOLTIP, approvalKey, approvalRequestBody, badgeText, badgeTooltip,
  canApprove, changedSinceApproval, changedTooltip, confirmText, conflictMessage, tabApproval,
} from './approval'

// FAR-88 §1 A1–A6 / FAR-90 §2.1 A1–A6: approve / remove the approval of the
// tab on screen. Each tab has its own approval entry on the plan; the badge
// and the button always follow the active tab. Both actions are confirmed in
// a modal, and the confirm button sends one request only.

export { APPROVED, canApprove } from './approval'

export default function ApprovalControl({
  plan, plantCode, onChange, tab, tabLabel, tabMonths, basePlan, modificationName,
}) {
  const { get, post } = useApi()
  const { user } = useAuth()
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false) // a second click before the re-render must not send again
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [confirming, setConfirming] = useState(null) // 'approve' | 'unapprove' | null

  const key = approvalKey(tab)
  if (!plan?.id || !key) return null
  const entry = tabApproval(plan, tab)
  const approved = !!entry
  const allowed = canApprove(user)
  const tabName = tabLabel || APPROVAL_TAB_NAMES[key]

  const close = () => { if (!inFlight.current) setConfirming(null) }

  const act = async (action) => {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const updated = await post(`/plans/${plan.id}/${action}`, approvalRequestBody(action, plantCode, tab, tabMonths))
      onChange?.(updated)
    } catch (e) {
      if (e?.status === 409) {
        // A6: someone else got there first. Refresh the plan and say so.
        const fresh = await get(`/plans/${plan.id}?plant=${plantCode}`).catch(() => null)
        if (fresh) onChange?.(fresh)
        setNotice(conflictMessage(action, tabName))
      } else {
        setError(e?.message || (action === 'approve' ? 'Could not approve this tab' : 'Could not remove the approval'))
      }
    } finally {
      inFlight.current = false
      setBusy(false)
      setConfirming(null)
    }
  }

  const ctx = {
    planName: plan.name,
    tabLabel: tabName,
    basePlanLabel: basePlanPillLabel(basePlan, !!plan.basePlanId),
    modificationName,
  }
  const text = confirming ? confirmText(confirming, ctx, entry) : null
  const changed = approved && changedSinceApproval(entry, plan)

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="approval-control" data-approval-key={key}>
      {approved && (
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{badgeTooltip(entry)}</span>}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }} data-testid="approved-badge">
            <Chip
              icon={<CheckCircle sx={{ fontSize: 16, color: 'inherit !important' }} />}
              label="Approved"
              size="small"
              sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600 }}
              data-testid="approved-chip"
            />
            <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }} data-testid="approved-stamp">
              {badgeText(entry)}
            </Typography>
          </Box>
        </Tooltip>
      )}
      {changed && (
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{changedTooltip(entry, plan)}</span>}>
          <Chip
            icon={<WarningAmber sx={{ fontSize: 16, color: 'inherit !important' }} />}
            label="Changed since approval"
            size="small"
            variant="outlined"
            sx={{ bgcolor: 'warning.light', color: 'warning.dark', borderColor: 'warning.main' }}
            data-testid="changed-since-approval"
          />
        </Tooltip>
      )}
      <Tooltip title={allowed ? '' : NOT_ALLOWED_TOOLTIP}>
        <span>
          {approved ? (
            // A4: neutral outline, not green.
            <Button size="small" variant="outlined" color="inherit" startIcon={<LockOpen />} disabled={!allowed || busy}
              onClick={() => setConfirming('unapprove')} data-testid="remove-approval"
              sx={{ borderColor: 'divider', color: 'text.primary', textTransform: 'none', whiteSpace: 'nowrap' }}>
              Remove Approval
            </Button>
          ) : (
            <Button size="small" variant="contained" color="primary" startIcon={<TaskAlt />} disabled={!allowed || busy}
              onClick={() => setConfirming('approve')} data-testid="approve"
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
              Approve
            </Button>
          )}
        </span>
      </Tooltip>
      {/* A6: a short message, off the header row so the pills don't reflow. */}
      <Snackbar open={!!(error || notice)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={notice ? 8000 : null} onClose={(_, reason) => { if (reason !== 'clickaway') { setError(null); setNotice(null) } }}>
        <Alert severity={error ? 'error' : 'warning'} variant="filled" onClose={() => { setError(null); setNotice(null) }}
          data-testid={error ? 'approval-error' : 'approval-notice'}>
          {error || notice}
        </Alert>
      </Snackbar>

      <Dialog open={!!confirming} onClose={close} data-testid="approval-dialog"
        aria-labelledby="approval-dialog-title" maxWidth="sm" fullWidth>
        {text && (
          <>
            <DialogTitle id="approval-dialog-title" sx={{ pr: 6 }}>
              {text.title}
              <IconButton aria-label="Close" onClick={close} disabled={busy} data-testid="approval-dialog-close"
                sx={{ position: 'absolute', right: 8, top: 8 }}>
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <DialogContentText data-testid="approval-dialog-body">{text.body}</DialogContentText>
              {text.details.length > 0 && (
                <Box sx={{ mt: 1.5 }} data-testid="approval-dialog-details">
                  {text.details.map((d) => (
                    <Typography key={d} variant="body2" color="text.secondary">{d}</Typography>
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button variant="outlined" color="inherit" onClick={close} disabled={busy} data-testid="approval-cancel"
                sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button variant="contained" color={confirming === 'unapprove' ? 'error' : 'primary'} disabled={busy}
                onClick={() => act(confirming)} data-testid="approval-confirm" sx={{ textTransform: 'none' }}>
                {text.confirm}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  )
}
